import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp, ROLES } from '../../context/AppContext';
import { DataTable } from '../../components/shared/DataTable';
import { KPICard } from '../../components/shared/KPICard';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Paperclip,
} from 'lucide-react';

export default function CaseDeletionRequests() {
  const navigate = useNavigate();
  const {
    currentRole,
    currentUserId,
    currentUser,
    deletionRequestsList,
    approveDeletionRequest,
    rejectDeletionRequest
  } = useApp();

  const [statusFilter, setStatusFilter] = useState('all');
  const [showActionDialog, setShowActionDialog] = useState(false);
  const [requestToAction, setRequestToAction] = useState(null);
  const [actionType, setActionType] = useState('approve'); // approve or reject
  const [adminRemark, setAdminRemark] = useState('');

  // Safeguard: strictly allow only Admins and Supreme Managers
  const isAuthorized = [ROLES.ADMIN, ROLES.SUPREME_MANAGER].includes(currentRole);

  if (!isAuthorized) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] text-center space-y-3">
        <XCircle className="w-16 h-16 text-red-500" />
        <h2 className="text-xl font-bold text-slate-800">Access Denied</h2>
        <p className="text-slate-500 max-w-md">
          You do not have permission to view this page. Only Admins and Supreme Managers are allowed.
        </p>
        <Button onClick={() => navigate('/')} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold">
          Return to Dashboard
        </Button>
      </div>
    );
  }

  // Calculate stats based on global deletion list
  const totalRequests = deletionRequestsList.length;
  const pendingRequests = deletionRequestsList.filter(r => r.status === 'pending').length;
  const approvedRequests = deletionRequestsList.filter(r => r.status === 'approved').length;
  const rejectedRequests = deletionRequestsList.filter(r => r.status === 'rejected').length;

  // Filter requests list by active status selection
  const filteredRequests = deletionRequestsList.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    return true;
  });

  const handleConfirmAction = () => {
    if (!adminRemark.trim() || !requestToAction) {
      toast.error('Admin remark is required.');
      return;
    }

    const userName = currentUser?.name || 'Administrator';

    if (actionType === 'approve') {
      approveDeletionRequest(requestToAction.id, adminRemark, userName);
      toast.success(`Deletion request for Case ${requestToAction.refNumber} has been approved.`);
    } else {
      rejectDeletionRequest(requestToAction.id, adminRemark, userName);
      toast.success(`Deletion request for Case ${requestToAction.refNumber} has been rejected.`);
    }

    setShowActionDialog(false);
    setRequestToAction(null);
    setAdminRemark('');
  };

  const columns = [
    {
      key: 'refNumber',
      header: 'Case ID',
      sortable: true,
      render: (row) => (
        <div>
          <p
            className="font-semibold text-blue-600 hover:underline cursor-pointer"
            onClick={() => navigate(`/cases/${row.caseId}`)}
          >
            {row.refNumber}
          </p>
          <p className="text-xs text-slate-500">{row.product}</p>
        </div>
      ),
    },
    {
      key: 'clientName',
      header: 'Client',
      sortable: true,
      render: (row) => (
        <span className="text-sm font-semibold text-slate-800">{row.clientName}</span>
      ),
    },
    {
      key: 'applicantName',
      header: 'Applicant Details',
      sortable: true,
      render: (row) => (
        <div>
          <p className="text-sm text-slate-800 font-semibold">{row.applicantName}</p>
          <p className="text-xs text-slate-500">Requested by: {row.requestedBy}</p>
        </div>
      ),
    },
    {
      key: 'reason',
      header: 'Reason & Remarks',
      render: (row) => (
        <div className="max-w-xs space-y-1">
          <Badge className="bg-red-50 text-red-700 border border-red-200 text-[10px] font-semibold uppercase px-1.5 h-5 rounded">
            {row.reason}
          </Badge>
          <p className="text-xs text-slate-600 leading-normal" title={row.remarks}>
            {row.remarks}
          </p>
        </div>
      ),
    },
    {
      key: 'attachmentName',
      header: 'Attachment',
      render: (row) => row.attachmentName ? (
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            toast.info(`Downloading supporting document: ${row.attachmentName}`);
          }}
          className="flex items-center gap-1 text-xs text-blue-600 hover:underline font-medium"
        >
          <Paperclip className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate max-w-[120px]">{row.attachmentName}</span>
        </a>
      ) : (
        <span className="text-xs text-slate-400">None</span>
      ),
    },
    {
      key: 'requestDate',
      header: 'Requested Date',
      sortable: true,
      render: (row) => (
        <span className="text-xs text-slate-500">
          {format(new Date(row.requestDate), 'MMM dd, yyyy, hh:mm a')}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row) => (
        <div className="space-y-1">
          <StatusBadge
            status={row.status === 'approved' ? 'success' : row.status === 'rejected' ? 'danger' : 'warning'}
            label={row.status === 'approved' ? 'Approved' : row.status === 'rejected' ? 'Rejected' : 'Pending'}
          />
          {row.adminRemark && (
            <p className="text-[10px] text-slate-500 italic max-w-[150px] truncate" title={row.adminRemark}>
              Note: {row.adminRemark}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (row) => {
        if (row.status === 'pending') {
          return (
            <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-8 px-2 bg-transparent text-xs font-semibold gap-1"
                onClick={() => {
                  setRequestToAction(row);
                  setActionType('approve');
                  setAdminRemark('');
                  setShowActionDialog(true);
                }}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Approve
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-red-600 border-red-200 hover:bg-red-50 h-8 px-2 bg-transparent text-xs font-semibold gap-1"
                onClick={() => {
                  setRequestToAction(row);
                  setActionType('reject');
                  setAdminRemark('');
                  setShowActionDialog(true);
                }}
              >
                <XCircle className="w-3.5 h-3.5" />
                Reject
              </Button>
            </div>
          );
        }
        return (
          <span className="text-xs text-slate-500 font-medium">
            {row.status === 'approved'
              ? `Approved by ${row.approvedBy || 'Admin'}`
              : `Rejected by ${row.rejectedBy || 'Admin'}`}
          </span>
        );
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header Title Section */}
      <div className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-bold text-slate-800 tracking-tight">Case Deletion Requests</h1>
        <p className="text-sm text-slate-500">
          Review, approve, or reject case removal requests raised by Clients.
        </p>
      </div>

      {/* KPI Cards Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard
          title="Total Requests"
          value={totalRequests}
          icon={FileText}
          iconBg="bg-blue-50"
          iconColor="text-blue-600"
        />
        <KPICard
          title="Pending Approval"
          value={pendingRequests}
          icon={Clock}
          iconBg="bg-amber-50"
          iconColor="text-amber-600"
        />
        <KPICard
          title="Approved Requests"
          value={approvedRequests}
          icon={CheckCircle2}
          iconBg="bg-emerald-50"
          iconColor="text-emerald-600"
        />
        <KPICard
          title="Rejected Requests"
          value={rejectedRequests}
          icon={XCircle}
          iconBg="bg-rose-50"
          iconColor="text-rose-600"
        />
      </div>

      {/* Table & Filtering */}
      <div className="crm-card bg-white p-6 border border-slate-100 rounded-xl space-y-4">
        <div className="flex items-center justify-between pb-2">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
              Filter Status:
            </span>
            <div className="flex bg-slate-100 rounded-lg p-0.5 border border-slate-200">
              {['all', 'pending', 'approved', 'rejected'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-3 py-1 text-xs font-semibold rounded-md transition-all ${
                    statusFilter === status
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filteredRequests}
          selectable={false}
          showFilters={false}
          searchPlaceholder="Search deletion requests..."
        />
      </div>

      {/* Approve/Reject Confirmation Modal Dialog */}
      <Dialog open={showActionDialog} onOpenChange={setShowActionDialog}>
        <DialogContent className="bg-white border border-slate-200 rounded-xl max-w-md p-6">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-slate-800 flex items-center gap-2">
              {actionType === 'approve' ? (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Approve Deletion Request
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Reject Deletion Request
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              You are about to {actionType} the deletion request for case <strong>{requestToAction?.refNumber}</strong> (Applicant: {requestToAction?.applicantName}).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-3">
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100 text-xs space-y-1.5">
              <div className="flex justify-between">
                <span className="text-slate-500">Reason:</span>
                <span className="font-semibold text-slate-800">{requestToAction?.reason}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Explanation:</span>
                <span className="font-semibold text-slate-800 max-w-[220px] truncate" title={requestToAction?.remarks}>
                  {requestToAction?.remarks}
                </span>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-700">
                Admin Remark <span className="text-red-500">*</span>
              </Label>
              <textarea
                className="w-full min-h-[90px] rounded-md border border-slate-200 p-2.5 text-xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 bg-white"
                placeholder={`Enter mandatory justification for ${actionType === 'approve' ? 'approval' : 'rejection'}...`}
                value={adminRemark}
                onChange={(e) => setAdminRemark(e.target.value)}
              />
              <p className="text-[10px] text-slate-400">
                Provide detailed notes. This remark will be sent to the client and logged permanently.
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setShowActionDialog(false);
                setRequestToAction(null);
                setAdminRemark('');
              }}
              className="border-slate-200 text-xs h-8 bg-transparent"
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleConfirmAction}
              disabled={!adminRemark.trim()}
              className={`text-white text-xs font-semibold h-8 ${
                actionType === 'approve'
                  ? 'bg-emerald-600 hover:bg-emerald-700'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              Confirm {actionType === 'approve' ? 'Approval' : 'Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
